import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { reduxForm, Field, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { compose, withHandlers } from 'recompose';
import PromiseCreator from 'promise-creator';
import LogRocket from 'logrocket';

import Input from '../../Forms/Input';
import Button from '../../Button';
import fieldRequired from '../../../utils/fieldRequired';
import styles from '../SupportWidget.css';
import FormError from '../../Forms/FormError';
import { createTicketAction } from '../../../redux/support-widget';

const FeatureIdeaFields = () => (
  <React.Fragment>
    <Field
      name="subject"
      label="Summary of feature idea"
      placeholder="What feature would you like us to consider implementing?"
      component={Input}
      validate={fieldRequired}
    />
    <Field
      name="description"
      label="Why is this feature important?"
      placeholder="How this feature would improve your experience on the platform?"
      component={Input}
      validate={fieldRequired}
    />
  </React.Fragment>
);

const SupportTicketFields = ({ isFeatureIdea }) => (
  <React.Fragment>
    {!isFeatureIdea && (
      <React.Fragment>
        <Field
          name="userMails"
          label="Users affected (if applicable)"
          placeholder="Enter the names and emails of 2-3 affected users"
          component={Input}
          validate={fieldRequired}
        />
        <Field
          name="links"
          label="Relevant page on the site"
          placeholder="example: myuniversity.peoplegrove.com/hub/myu/programs-v2"
          component={Input}
          validate={fieldRequired}
        />
      </React.Fragment>
    )}
  </React.Fragment>
);

SupportTicketFields.propTypes = {
  isFeatureIdea: PropTypes.bool,
};

const IssueScreen = ({
  screenType,
  error,
  pristine,
  submitting,
  handleSubmit,
  formData,
}) => {
  const isFeatureIdea = useMemo(
    () => screenType === 'featureIdea',
    [screenType],
  );

  const isValid = useCallback(() => {
    const { subject, description } = formData;
    return subject && description;
  }, [formData]);

  return (
    <form onSubmit={handleSubmit}>
      <FormError error={error} />
      <Field
        name="subject"
        label="Subject"
        placeholder="Brief Summary"
        component={Input}
        validate={fieldRequired}
      />
      <Field
        name="description"
        label="How can we help?"
        placeholder="What are you trying to do? Where are you getting stuck?"
        component={Input}
        validate={fieldRequired}
      />

      <div className={styles.formAction}>
        <Button
          type="submit"
          disabled={pristine || !isValid()}
          loading={submitting}
          className={styles.button}
        >
          Submit Ticket
        </Button>
      </div>
    </form>
  );
};

IssueScreen.propTypes = {
  screenType: PropTypes.string.isRequired,
  error: PropTypes.string,
  pristine: PropTypes.bool,
  submitting: PropTypes.bool,
  handleSubmit: PropTypes.func,
  formData: PropTypes.object,
};

const form = 'support-issue-form';

export default compose(
  connect(
    (state) => ({
      formData: formValueSelector(form)(
        state,
        'subject',
        'description',
        'links',
        'userMails',
      ),
    }),
    {
      createTicket: createTicketAction,
    },
  ),
  withHandlers({
    handleSubmit:
      ({ createTicket, formData, screenType, closeWidget = () => {} }) =>
      (event) => {
        event.preventDefault();
        const { promise, resolve, reject } = PromiseCreator();
        const isFeatureIdea = screenType === 'featureIdea';
        createTicket({
          body: {
            title: formData.subject,
            type: isFeatureIdea
              ? 'New Feature Idea Submitted'
              : 'Bug / Technical Issue',
            issueType: isFeatureIdea ? 'New Feature Idea Submitted' : '',
            priority: isFeatureIdea && 'Low',
            issueField: {
              description: formData.description,
              issuePage: formData.links,
              accessibilityFollowUp: 'no',
            },
            files: null,
            userEmails: formData.userMails,
            logRocket: LogRocket.sessionURL,
          },
          resolve,
          reject,
        });
        return promise.then(() => {
          closeWidget();
        });
      },
  }),
  reduxForm({
    form,
    enableReinitialize: true,
  }),
)(IssueScreen);



// WEBPACK FOOTER //
// ./src/client/Components/SupportWidget/Screens/IssueScreen.js